import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').trim();

// Debounce threshold: prevent duplicate tracking within 2 seconds
const TRACKING_DEBOUNCE_MS = 2000;

/**
 * Maps route pathnames to page names for tracking
 */
function getPageNameFromRoute(pathname: string): string {
  if (pathname === '/disconnection-notice') return 'disconnection-notice';
  if (pathname === '/session-finalized') return 'session-finalized';
  if (pathname === '/post-game-questions') return 'post-game-questions';
  if (pathname === '/post-game-other-opinion') return 'post-game-other-opinion';
  if (pathname === '/post-game-risk') return 'post-game-risk';
  if (pathname === '/demographics') return 'demographics';
  if (pathname === '/prolific-thank-you') return 'prolific-thank-you';
  if (pathname === '/participation-acknowledged') return 'participation-acknowledged';
  
  // For root path or catch-all routes, return null to skip tracking
  // (AppContent will handle tracking via phases)
  return '';
}

/**
 * Maps app phases to page names for tracking
 */
export function getPageNameFromPhase(phase: string, meta?: Record<string, any>): string {
  switch (phase) {
    case 'INITIALIZING': return 'initializing';
    case 'TUTORIAL': 
      // If meta contains tutorial step, use specific tutorial page name
      if (meta?.tutorialStep !== undefined) {
        const step = meta.tutorialStep;
        switch (step) {
          case 0: return 'tutorial-consent';
          case 1: return 'tutorial-attention-check';
          case 2: return 'tutorial-instructions';
          case 3: return 'tutorial-discussion-screen';
          case 4: return 'tutorial-investment-round-screen';
          case 5: return 'tutorial-scenario-intro';
          case 6: return 'tutorial-scenario-one';
          case 7: return 'tutorial-scenario-two';
          case 8: return 'tutorial-scenario-three';
          case 9: return 'tutorial-investment-outcomes';
          default: return 'tutorial';
        }
      }
      if (meta?.failedAttentionCheck) {
        return 'tutorial-failed-attention-check';
      }
      return 'tutorial';
    case 'SETUP_ERROR': return 'setup-error';
    case 'WAITING_ROOM': return 'waiting-room';
    case 'PARTNER_MATCHED': return 'partner-matched';
    case 'PRACTICE_ROUND_INTRO': return 'practice-round-intro';
    case 'TUTORIAL_ROUND': return 'tutorial-round';
    case 'GAME': 
      // If meta contains round number and chat period info
      if (meta?.isChatPeriod === true && meta?.chatNumber !== undefined) {
        return `game.chat.${meta.chatNumber}`;
      }
      if (meta?.roundNumber !== undefined) {
        return `game.round.${meta.roundNumber}`;
      }
      return 'game';
    case 'GAME_OVER': return 'game-over';
    default: return 'unknown';
  }
}

// Store last tracking time per page to prevent duplicates
const lastTrackingTime = new Map<string, number>();
// Store last tracked page name to detect page changes
let lastTrackedPageName: string | null = null;
// Flag to prevent duplicate page close tracking
let pageCloseTracked = false;

/**
 * Clears debounce cache for a page when it changes
 */
function clearPageDebounce(pageName: string): void {
  if (lastTrackedPageName !== pageName) {
    // Page changed - clear all debounce entries for the previous page
    if (lastTrackedPageName) {
      // Remove all entries related to the old page
      const keysToDelete: string[] = [];
      lastTrackingTime.forEach((_, key) => {
        if (key.startsWith(lastTrackedPageName + '-')) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => lastTrackingTime.delete(key));
    }
    lastTrackedPageName = pageName;
  }
}

/**
 * Tracks a page to the backend with debouncing
 * @param pageName - The page name to track
 * @param prolificId - The prolific ID
 * @param meta - Optional metadata
 * @param force - If true, bypass debounce (for navigation/phase changes)
 */
async function trackPage(pageName: string, prolificId: string | null, meta?: Record<string, any>, force: boolean = false): Promise<void> {
  if (!prolificId || !prolificId.trim()) {
    return; // Skip if no prolificId
  }

  if (!API_BASE_URL) {
    console.warn('PageTracking: API_BASE_URL not set, skipping tracking');
    return;
  }

  // Create a unique key for this page + event type
  const eventType = meta?.event || 'navigation';
  const trackingKey = `${pageName}-${eventType}`;
  const now = Date.now();

  // If this is a navigation/phase change (force=true), clear debounce for page changes
  if (force && eventType === 'navigation') {
    clearPageDebounce(pageName);
  }

  // Debounce: skip if we tracked this page+event recently (unless forced)
  // Always allow tracking if:
  // 1. It's forced (navigation/phase changes)
  // 2. Page name changed (to catch rapid navigation)
  // 3. It's a beforeunload event (critical - never debounce)
  const pageChanged = lastTrackedPageName !== pageName;
  const isBeforeUnload = eventType === 'beforeunload';
  
  if (!force && !pageChanged && !isBeforeUnload) {
    const lastTime = lastTrackingTime.get(trackingKey) || 0;
    if (now - lastTime < TRACKING_DEBOUNCE_MS) {
      return; // Skip duplicate tracking only if page hasn't changed and it's not critical
    }
  }

  // Update last tracking time
  lastTrackingTime.set(trackingKey, now);
  
  // Update last tracked page name for navigation events (always track page changes)
  // This ensures we detect page changes even if events fire in quick succession
  if (force || eventType === 'navigation' || pageChanged) {
    lastTrackedPageName = pageName;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/pageTracking`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'prolific-id': prolificId,
      },
      body: JSON.stringify({
        page: pageName,
        url: window.location.href,
        clientTimestamp: now,
        meta: meta || {},
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.warn('PageTracking: Request failed:', response.status, errorText);
    }
  } catch (error) {
    // Silently fail - don't break the app
    console.warn('PageTracking: Error tracking page:', error);
  }
}

/**
 * Hook to track pages based on route changes
 * Tracks on navigation and window closing (beforeunload)
 */
export function usePageTracking(prolificId: string | null): void {
  const location = useLocation();
  const lastPageRef = useRef<string | null>(null);

  useEffect(() => {
    if (!prolificId) return;

    const pageName = getPageNameFromRoute(location.pathname);

    // Skip tracking if pageName is empty (root path handled by AppContent phases)
    if (!pageName) {
      lastPageRef.current = null;
      return;
    }

    // Track immediately on mount and route change
    const trackCurrentPage = () => {
      // Only track if page actually changed (unless it's the first time)
      if (pageName !== lastPageRef.current || lastPageRef.current === null) {
        lastPageRef.current = pageName;
        // Force tracking on navigation (bypass debounce)
        trackPage(pageName, prolificId, undefined, true);
      }
    };

    // Track immediately on navigation (forced to bypass debounce)
    trackCurrentPage();
    
    // Reset page close flag on navigation (new page, new close tracking)
    pageCloseTracked = false;

    // Track on window closing (critical event - always track)
    // Use multiple events for maximum reliability
    const trackPageClose = () => {
      // Prevent duplicate close tracking (multiple events may fire)
      if (pageCloseTracked) {
        console.log('PageTracking: Page close already tracked, skipping duplicate');
        return;
      }
      pageCloseTracked = true;
      
      if (!API_BASE_URL || !prolificId) {
        console.warn('PageTracking: Cannot track page close - missing API_BASE_URL or prolificId');
        return;
      }
      
      const data = JSON.stringify({
        page: `${pageName}.close`,
        url: window.location.href,
        clientTimestamp: Date.now(),
        meta: { event: 'page-close' },
      });
      
      console.log('PageTracking: Attempting to track page close:', pageName);
      
      // Primary: Use fetch with keepalive (supports headers, works on unload)
      // This is more reliable than sendBeacon because it supports custom headers
      try {
        const fetchResult = fetch(`${API_BASE_URL}/pageTracking`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'prolific-id': prolificId,
          },
          body: data,
          keepalive: true, // Critical: keeps request alive even after page unloads
        });
        
        // Log success (but don't await - page is closing)
        fetchResult.then(() => {
          console.log('PageTracking: Page close tracked successfully via fetch');
        }).catch(err => {
          console.warn('PageTracking: Fetch failed, trying sendBeacon:', err);
          // Fallback to sendBeacon if fetch fails
          if (navigator.sendBeacon) {
            const url = `${API_BASE_URL}/pageTracking?prolific-id=${encodeURIComponent(prolificId)}`;
            const sent = navigator.sendBeacon(
              url,
              new Blob([data], { type: 'application/json' })
            );
            console.log('PageTracking: sendBeacon result:', sent);
          }
        });
      } catch (e) {
        console.warn('PageTracking: Fetch error, trying sendBeacon:', e);
        // Fallback: Use sendBeacon (doesn't support headers, so prolific-id in URL)
        if (navigator.sendBeacon) {
          const url = `${API_BASE_URL}/pageTracking?prolific-id=${encodeURIComponent(prolificId)}`;
          const sent = navigator.sendBeacon(
            url,
            new Blob([data], { type: 'application/json' })
          );
          console.log('PageTracking: sendBeacon result:', sent);
        } else {
          console.error('PageTracking: Both fetch and sendBeacon failed');
        }
      }
    };

    // Use multiple events for maximum reliability
    // pagehide is more reliable than beforeunload in modern browsers
    const handlePageHide = () => {
      trackPageClose();
    };

    // beforeunload as backup (may not fire in all browsers/tabs)
    const handleBeforeUnload = () => {
      trackPageClose();
    };

    // unload as final fallback
    const handleUnload = () => {
      trackPageClose();
    };

    // Add multiple event listeners for maximum coverage
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);

    // Cleanup event listeners on unmount or route change
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
    };
  }, [location.pathname, prolificId]); // Re-run on route change
}

/**
 * Hook to track pages based on app phase changes (for AppContent)
 * Tracks on phase changes and window closing (beforeunload)
 */
export function usePhaseTracking(phase: string, prolificId: string | null, meta?: Record<string, any>): void {
  const lastPageRef = useRef<string | null>(null);
  const lastPageNameRef = useRef<string | null>(null);

  useEffect(() => {
    // Skip tracking if meta.skip is true (used to prevent double tracking)
    if (meta?.skip) {
      return;
    }

    if (!prolificId) return;

    const pageName = getPageNameFromPhase(phase, meta);
    // Create a unique key for tracking (phase + meta to detect changes)
    const trackingKey = JSON.stringify({ phase, meta });

    // Track immediately on mount and phase/meta change
    const trackCurrentPhase = () => {
      // Track if:
      // 1. Tracking key changed (phase/meta changed)
      // 2. Page name changed (to catch rapid navigation)
      // 3. First time tracking
      const pageNameChanged = pageName !== lastPageNameRef.current;
      const trackingKeyChanged = trackingKey !== lastPageRef.current;
      
      if (trackingKeyChanged || pageNameChanged || lastPageRef.current === null) {
        lastPageRef.current = trackingKey;
        lastPageNameRef.current = pageName;
        // Force tracking on phase change (bypass debounce)
        trackPage(pageName, prolificId, meta, true);
      }
    };

    // Track immediately on phase change (forced to bypass debounce)
    trackCurrentPhase();
    
    // Reset page close flag on phase change (new page, new close tracking)
    pageCloseTracked = false;

    // Track on window closing (critical event - always track)
    // Use multiple events for maximum reliability
    const trackPageClose = () => {
      // Prevent duplicate close tracking (multiple events may fire)
      if (pageCloseTracked) {
        console.log('PageTracking: Page close already tracked, skipping duplicate');
        return;
      }
      pageCloseTracked = true;
      
      if (!API_BASE_URL || !prolificId) {
        console.warn('PageTracking: Cannot track page close - missing API_BASE_URL or prolificId');
        return;
      }
      
      const data = JSON.stringify({
        page: `${pageName}.close`,
        url: window.location.href,
        clientTimestamp: Date.now(),
        meta: { ...meta, event: 'page-close' },
      });
      
      console.log('PageTracking: Attempting to track page close:', pageName);
      
      // Primary: Use fetch with keepalive (supports headers, works on unload)
      // This is more reliable than sendBeacon because it supports custom headers
      try {
        const fetchResult = fetch(`${API_BASE_URL}/pageTracking`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'prolific-id': prolificId,
          },
          body: data,
          keepalive: true, // Critical: keeps request alive even after page unloads
        });
        
        // Log success (but don't await - page is closing)
        fetchResult.then(() => {
          console.log('PageTracking: Page close tracked successfully via fetch');
        }).catch(err => {
          console.warn('PageTracking: Fetch failed, trying sendBeacon:', err);
          // Fallback to sendBeacon if fetch fails
          if (navigator.sendBeacon) {
            const url = `${API_BASE_URL}/pageTracking?prolific-id=${encodeURIComponent(prolificId)}`;
            const sent = navigator.sendBeacon(
              url,
              new Blob([data], { type: 'application/json' })
            );
            console.log('PageTracking: sendBeacon result:', sent);
          }
        });
      } catch (e) {
        console.warn('PageTracking: Fetch error, trying sendBeacon:', e);
        // Fallback: Use sendBeacon (doesn't support headers, so prolific-id in URL)
        if (navigator.sendBeacon) {
          const url = `${API_BASE_URL}/pageTracking?prolific-id=${encodeURIComponent(prolificId)}`;
          const sent = navigator.sendBeacon(
            url,
            new Blob([data], { type: 'application/json' })
          );
          console.log('PageTracking: sendBeacon result:', sent);
        } else {
          console.error('PageTracking: Both fetch and sendBeacon failed');
        }
      }
    };

    // Use multiple events for maximum reliability
    // pagehide is more reliable than beforeunload in modern browsers
    const handlePageHide = () => {
      trackPageClose();
    };

    // beforeunload as backup (may not fire in all browsers/tabs)
    const handleBeforeUnload = () => {
      trackPageClose();
    };

    // unload as final fallback
    const handleUnload = () => {
      trackPageClose();
    };

    // Add multiple event listeners for maximum coverage
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);

    // Cleanup event listeners on unmount or phase/meta change
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
    };
  }, [phase, prolificId, JSON.stringify(meta)]); // Re-run on phase or meta change
}

